import React, { useCallback, useEffect, useRef, useState } from 'react';

import CustomLoading from '../../../util_components/custom_loading';
import DataTable from '../shared/data_table';
import LoadFailed from '../shared/load_failed';
import StatusPill from '../shared/status_pill';
import { Ledger, StatusCell, What, Who } from '../shared/cells';
import TpDialog from '../shared/tp_dialog';
import { discard_guard_text } from '../shared/dialog_logic';
import { ist_day } from '../shared/ist';
import { fetch_payout_teacher_month, post_payout_paid_outside, post_payout_retry } from '../api';
import {
  ACTION_PAID_OUTSIDE,
  BANK_PAID_NEEDS_INVOICE_TEXT,
  ACTION_RETRY,
  PARTIAL_NEEDS_INVOICE_TEXT,
  REASON_MAX_LEN,
  confirm_error,
  confirm_fields,
  confirm_needs,
  full_amount_text,
  has_error,
  paid_outside_errors,
  payout_error,
  payout_reason_error,
  retry_confirm_summary,
  rupees_paise,
  typed_amount_paise,
  teacher_month_view
} from './adapters';
import { paid_outside_body, retry_body } from './requests';
import useIdempotentPost from './use_idempotent_post';
import ConfirmStep from './confirm_step';
import ListCard from './list_card';
import DownloadCsvButton from '../../../util_components/csv_export/download_csv_button';
import { ITEM_COLUMNS, payouts_csv_name } from './csv';

const LOAD_FAILED_TEXT = 'Could not load this teacher-month.';
const RETRY_FAILED_TEXT = 'The retry may not have been saved. Reload to check.';
const OUTSIDE_FAILED_TEXT = 'The payment may not have been recorded. Reload to check.';

function RetryForm({ item, on_done, on_error, on_cancel, on_busy }) {
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const { busy, send } = useIdempotentPost(RETRY_FAILED_TEXT, undefined, on_busy);
  const [step2, setStep2] = useState({ amount: '', checked: false });
  const reason_err = payout_reason_error(reason);
  const needs = confirm_needs(ACTION_RETRY);
  const step2_error = confirm_error(needs, step2, rupees_paise(item.amount_raw));

  const submit = () => {
    // The route takes no client_request_id: the server refuses a second retry (PAYOUT_ITEM_NOT_FAILED).
    const body = retry_body(item.uuid, reason, confirm_fields(needs, step2));
    send(body, () => post_payout_retry(body)).then((r) => {
      if (r.ok) {
        on_done(`The ${item.amount} line goes in the next bank file with the same details.`);
        return;
      }
      if (r.err.kind === 'auth') on_error(r.raw, RETRY_FAILED_TEXT);
      setError(r.err.text);
      setConfirming(false);
    });
  };

  return (
    <div className="tp-po-form" role="group" aria-label="Retry with same details">
      {confirming ? (
        <ConfirmStep
          id="tp-po-retry-confirm"
          action={ACTION_RETRY}
          needs={needs}
          summary={[retry_confirm_summary(item)]}
          warn
          input={step2}
          on_input={setStep2}
          error={step2.amount.trim() || step2.checked ? step2_error : ''}
        />
      ) : (
        <div className="tp-field">
          <label htmlFor="tp-po-retry-reason">Why retry with the same details (recorded)</label>
          <textarea
            id="tp-po-retry-reason"
            className="tp-textarea"
            value={reason}
            maxLength={REASON_MAX_LEN * 2}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            placeholder="For example: the teacher confirmed the account is open again."
          />
          {touched && reason_err ? (
            <span className="tp-neg" role="alert">
              {reason_err}
            </span>
          ) : null}
        </div>
      )}
      {error ? (
        <div className="tp-neg" role="alert">
          {error}
        </div>
      ) : null}
      <div className="tp-modal-ft">
        {confirming ? (
          <button
            type="button"
            className="tp-btn-ghost"
            onClick={busy ? undefined : () => setConfirming(false)}
          >
            Back
          </button>
        ) : (
          <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_cancel}>
            Cancel
          </button>
        )}
        {confirming ? (
          <button
            type="button"
            className="tp-btn"
            onClick={busy || step2_error ? undefined : submit}
            disabled={!!step2_error}
            aria-disabled={busy ? 'true' : undefined}
          >
            {busy ? 'Saving…' : 'Yes, retry'}
          </button>
        ) : (
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => {
              setTouched(true);
              if (!reason_err) setConfirming(true);
            }}
          >
            Retry with same details…
          </button>
        )}
      </div>
    </div>
  );
}

function PaidOutsideForm({ view, today, on_done, on_error, on_cancel, on_busy }) {
  const upfront_full = view.outside_full_only;
  const bank_paid = view.bank_paid_other_raw;
  // Only a bank amount below what is owed is recorded as such; a larger one means record the full outstanding.
  const bank_less = bank_paid !== null && view.bank_paid_less;
  const initial_amount = () => {
    // Without an invoice the bank's amount can't be recorded yet, so nothing is prefilled.
    if (bank_less) return upfront_full ? '' : full_amount_text(bank_paid);
    if (bank_paid !== null) return full_amount_text(view.outstanding_raw);
    return upfront_full ? full_amount_text(view.outstanding_raw) : '';
  };
  const [form, setForm] = useState(() => ({
    amount: initial_amount(),
    paid_on: '',
    reference: '',
    reason: ''
  }));
  const [touched, setTouched] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  // Locked to the whole outstanding: known up front from has_confirmed_invoice, else set by a
  // PAYOUT_PARTIAL_NEEDS_INVOICE refusal (older server, or an invoice withdrawn since the load).
  const [full_only, setFullOnly] = useState(upfront_full);
  const { busy, send } = useIdempotentPost(OUTSIDE_FAILED_TEXT, undefined, on_busy);
  const errors = paid_outside_errors(
    form,
    view.outstanding_raw,
    today,
    full_only,
    bank_less ? bank_paid : null
  );
  const [step2, setStep2] = useState({ amount: '', checked: false });
  const needs = confirm_needs(ACTION_PAID_OUTSIDE);
  const step2_error = confirm_error(needs, step2, typed_amount_paise(form.amount));
  const set = (k) => (e) => {
    setConfirming(false);
    setStep2({ amount: '', checked: false });
    setForm((f) => ({ ...f, [k]: e.target.value }));
  };
  const field_error = (k) =>
    touched && errors[k] ? (
      <span className="tp-neg" role="alert">
        {errors[k]}
      </span>
    ) : null;

  const submit = () => {
    const signature = paid_outside_body({
      ...form,
      teacher_uuid: view.teacher_uuid,
      pay_month: view.pay_month,
      client_request_id: '',
      additional_payment_uuid: '',
      ...confirm_fields(needs, step2)
    });
    send(signature, (id) => post_payout_paid_outside({ ...signature, client_request_id: id })).then(
      (r) => {
        if (r.ok) {
          on_done(
            `Recorded ₹${form.amount.trim()} paid outside the bank file for ${view.teacher_name}.`
          );
          return;
        }
        if (r.err.kind === 'auth') on_error(r.raw, OUTSIDE_FAILED_TEXT);
        if (r.err.kind === 'full_only') {
          setFullOnly(true);
          setStep2({ amount: '', checked: false });
          setForm((f) => ({ ...f, amount: full_amount_text(view.outstanding_raw) || f.amount }));
        }
        setError(r.err.text);
        setConfirming(false);
      }
    );
  };

  return (
    <div
      className="tp-po-form"
      role="group"
      aria-label="Record a payment made outside the bank file"
    >
      <div className="tp-hint">
        Only for money already paid another way (for example to a deleted account&apos;s last
        approved bank account). It counts as paid and never goes in a bank file. Still owed:{' '}
        {view.outstanding}
        {view.withheld
          ? ` (after withholding ${view.withheld} to recover a duplicate payment)`
          : ''}
        .
      </div>
      <div className="tp-field-2">
        <div className="tp-field">
          <label htmlFor="tp-po-out-amount">Amount paid (₹)</label>
          <input
            id="tp-po-out-amount"
            className="tp-input"
            inputMode="decimal"
            value={form.amount}
            onChange={set('amount')}
            readOnly={full_only && !bank_less}
            aria-describedby={full_only ? 'tp-po-out-amount-full' : undefined}
            placeholder="2500.00"
          />
          {bank_paid !== null ? (
            <span className="tp-hint">
              The bank paid {view.bank_paid_other} for an earlier payment of this month.{' '}
              {bank_less && full_only ? BANK_PAID_NEEDS_INVOICE_TEXT : null}
              {bank_less && !full_only ? 'Record that amount.' : null}
              {bank_less
                ? null
                : 'That is more than this month owes: record the full outstanding and raise the extra with the payments lead.'}
            </span>
          ) : null}
          {full_only && !bank_less ? (
            <span id="tp-po-out-amount-full" className="tp-hint">
              {upfront_full
                ? PARTIAL_NEEDS_INVOICE_TEXT
                : `No confirmed invoice: the full ${view.outstanding} still owed.`}
            </span>
          ) : null}
          {field_error('amount')}
        </div>
        <div className="tp-field">
          <label htmlFor="tp-po-out-date">Paid on</label>
          <input
            id="tp-po-out-date"
            className="tp-input"
            type="date"
            max={today}
            value={form.paid_on}
            onChange={set('paid_on')}
          />
          {field_error('paid_on')}
        </div>
      </div>
      <div className="tp-field">
        <label htmlFor="tp-po-out-ref">Payment reference (UTR or receipt)</label>
        <input
          id="tp-po-out-ref"
          className="tp-input"
          value={form.reference}
          onChange={set('reference')}
          maxLength={64}
        />
        {field_error('reference')}
      </div>
      <div className="tp-field">
        <label htmlFor="tp-po-out-reason">Reason (recorded)</label>
        <textarea
          id="tp-po-out-reason"
          className="tp-textarea"
          value={form.reason}
          maxLength={REASON_MAX_LEN * 2}
          onChange={set('reason')}
        />
        {field_error('reason')}
      </div>
      {confirming ? (
        <ConfirmStep
          id="tp-po-out-confirm"
          action={ACTION_PAID_OUTSIDE}
          needs={needs}
          summary={[
            `Record ₹${form.amount.trim()} paid to ${view.teacher_name} on ${form.paid_on}, reference ` +
              `${form.reference.trim()}? The teacher sees it as paid separately.`
          ]}
          warn
          input={step2}
          on_input={setStep2}
          error={step2.amount.trim() ? step2_error : ''}
        />
      ) : null}
      {error ? (
        <div className="tp-neg" role="alert">
          {error}
        </div>
      ) : null}
      <div className="tp-modal-ft">
        <button type="button" className="tp-btn-ghost" onClick={busy ? undefined : on_cancel}>
          Cancel
        </button>
        {confirming ? (
          <button
            type="button"
            className="tp-btn"
            onClick={busy || step2_error ? undefined : submit}
            disabled={!!step2_error}
            aria-disabled={busy ? 'true' : undefined}
          >
            {busy ? 'Saving…' : 'Yes, record it'}
          </button>
        ) : (
          <button
            type="button"
            className="tp-btn-out"
            onClick={() => {
              setTouched(true);
              if (!has_error(errors)) setConfirming(true);
            }}
          >
            Record payment…
          </button>
        )}
      </div>
    </div>
  );
}

// Four tracks (list-row pattern): round · references · amount · state and Retry; cards below 660px.
const item_columns = (on_retry) => {
  const retry_button = (r, className) =>
    r.retry_allowed ? (
      <button
        type="button"
        className={className}
        aria-label={`Retry the ${r.round_no} payment with the same details`}
        onClick={() => on_retry(r)}
      >
        Retry with same details
      </button>
    ) : null;
  const chip = (r) => <StatusPill tone={r.state_view.tone}>{r.state_view.label}</StatusPill>;
  const meta = (r) => [r.why || null];
  return [
    {
      key: 'round_no',
      role: 'identifier',
      label: 'Round',
      track: { compact: '26%', full: '24%' },
      render: (r) => (
        <Who name={r.round_no} meta={[`Sent ${r.sent_on}`, r.account, r.branch_line]} />
      )
    },
    {
      key: 'reference',
      label: 'References',
      render: (r) => (
        <What
          title={<span className="tp-mono tp-po-ref">{r.reference}</span>}
          lines={[
            <>
              Bank ref <span className="tp-mono">{r.bank_reference}</span>
            </>,
            r.resolution_note || null
          ]}
        />
      )
    },
    {
      key: 'amount',
      role: 'amount',
      label: 'Amount',
      align: 'right',
      track: { compact: '140px', full: '148px' },
      render: (r) => <span className="tp-c-amt">{r.amount}</span>,
      card_render: (r) => <Ledger items={[{ label: 'Amount', value: r.amount }]} />
    },
    {
      key: 'state',
      role: 'status',
      label: 'State',
      track: { compact: '160px', full: '176px' },
      render: (r) => (
        <StatusCell
          chip={chip(r)}
          meta={meta(r)}
          actions={
            r.retry_allowed ? (
              <span className="tp-c-acts tp-actions">{retry_button(r, 'tp-link')}</span>
            ) : null
          }
        />
      ),
      card_render: (r) => <StatusCell chip={chip(r)} meta={meta(r)} />
    },
    {
      key: 'action',
      label: 'Action',
      role: 'actions',
      in_table: false,
      card_render: (r) => retry_button(r, 'tp-btn-out')
    }
  ];
};

function TeacherMonthDialog({ pay_month, teacher_uuid, now, on_close, on_changed, on_error }) {
  const [status, setStatus] = useState('loading');
  const [view, setView] = useState(null);
  const [load_error, setLoadError] = useState('');
  const [form, setForm] = useState(null); // {kind:'retry', item} | {kind:'outside'}
  const [notice, setNotice] = useState('');
  // A write in flight locks the dialog: closing and reopening would mint a new request id.
  const [busy, setBusy] = useState(false);
  const seq = useRef(0);
  const today = ist_day(now());

  const load = useCallback(() => {
    seq.current += 1;
    const mine = seq.current;
    setStatus('loading');
    fetch_payout_teacher_month(pay_month, teacher_uuid)
      .then((d) => {
        if (mine !== seq.current) return;
        setView(teacher_month_view(d));
        setStatus('ready');
      })
      .catch((e) => {
        if (mine !== seq.current) return;
        const v = payout_error(e, LOAD_FAILED_TEXT);
        if (v.kind === 'auth') on_error(e, LOAD_FAILED_TEXT);
        setLoadError(v.text);
        setStatus('error');
      });
  }, [pay_month, teacher_uuid, on_error]);

  useEffect(() => {
    load();
  }, [load]);

  const done = (text) => {
    setForm(null);
    setNotice(text);
    on_changed(text);
    load();
  };

  return (
    <TpDialog
      title={view ? `${view.teacher_name} · ${pay_month}` : `Teacher-month ${pay_month}`}
      size="lg"
      on_close={on_close}
      busy={busy}
      guard_text={form ? discard_guard_text('form open') : ''}
    >
      <div className="tp-modal tp-po-tm">
        {status === 'loading' ? <CustomLoading /> : null}
        {status === 'error' ? <LoadFailed text={load_error} on_retry={load} /> : null}
        {status === 'ready' && view ? (
          <>
            {notice ? (
              <div className="tp-banner tp-banner-ok" role="status">
                {notice}
              </div>
            ) : null}
            <div className="tp-stats tp-po-tm-stats">
              <div className="tp-stat">
                <span className="tp-stat-label">Month status</span>
                <span className="tp-stat-value tp-po-tm-word">{view.lifecycle || '—'}</span>
              </div>
              <div className="tp-stat">
                <span className="tp-stat-label">Payable</span>
                <span className="tp-stat-value tp-nowrap">{view.payable}</span>
              </div>
              <div className="tp-stat">
                <span className="tp-stat-label">Paid so far</span>
                <span className="tp-stat-value tp-nowrap">{view.paid_so_far}</span>
              </div>
              {view.withheld ? (
                <div className="tp-stat">
                  <span className="tp-stat-label">Withheld</span>
                  <span className="tp-stat-value tp-nowrap">{view.withheld}</span>
                </div>
              ) : null}
              <div className="tp-stat tp-po-tm-owed">
                <span className="tp-stat-label">Still owed</span>
                <span className="tp-stat-value tp-nowrap">{view.outstanding}</span>
              </div>
            </div>
            {view.recoveries.map((x) => (
              <div className="tp-banner tp-banner-warn" key={x.label}>
                {x.label}: {x.amount} withheld from this payout
              </div>
            ))}
            <div className="tp-banner tp-banner-info">Next bank file: {view.eligibility_text}</div>
            <div className="tp-banner tp-banner-info tp-po-sees">
              <span>The teacher sees:</span>
              <StatusPill tone={view.teacher_state.tone}>{view.teacher_state.label}</StatusPill>
              <span>{view.teacher_headline}</span>
            </div>

            <ListCard
              title="Payments"
              sub={`${view.items.length}`}
              acts={
                <DownloadCsvButton
                  columns={ITEM_COLUMNS}
                  rows={view.items}
                  file_name={payouts_csv_name(
                    `Teacher month ${view.teacher_uuid || teacher_uuid}`,
                    pay_month
                  )}
                />
              }
            >
              <DataTable
                columns={item_columns((item) => setForm({ kind: 'retry', item }))}
                rows={view.items}
                rowKey={(r) => r.uuid}
                emptyText="No payment lines yet."
                card_below={600}
              />
            </ListCard>
            {form && form.kind === 'retry' ? (
              <RetryForm
                item={form.item}
                on_done={done}
                on_error={on_error}
                on_busy={setBusy}
                on_cancel={() => setForm(null)}
              />
            ) : null}

            {view.claims.length ? (
              <>
                <h3 className="tp-po-h3">Payment not received</h3>
                <ul className="tp-po-claims">
                  {view.claims.map((c) => (
                    <li key={c.claim_uuid}>
                      <StatusPill tone={c.status_view.tone}>{c.status_view.label}</StatusPill>{' '}
                      {c.amount} sent {c.sent_on}, reported {c.created_at}
                      {c.note ? `: "${c.note}"` : ''}
                      {c.resolution_note ? ` Resolved: ${c.resolution_note}` : ''}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {view.can_pay_outside && !form ? (
              <div className="tp-modal-ft">
                <button
                  type="button"
                  className="tp-btn-out"
                  onClick={() => setForm({ kind: 'outside' })}
                >
                  Record a payment made outside the bank file…
                </button>
              </div>
            ) : null}
            {form && form.kind === 'outside' ? (
              <PaidOutsideForm
                view={view}
                today={today}
                on_done={done}
                on_error={on_error}
                on_busy={setBusy}
                on_cancel={() => setForm(null)}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </TpDialog>
  );
}

export default TeacherMonthDialog;
